import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

import type {
  CrossPlatformPrefill,
  RegisteredPlatform,
  Verify2faOnboardingResponse,
} from '@app/core/models';

/**
 * CW-13h — Cross-platform onboarding state (LD-9 / spec §6.2).
 *
 * Signal-based, in-memory only. Holds the Branch B payload returned by
 * `verify-2fa` so the consent modal and onboarding wizard pages can
 * detect cross-platform mode and prefill identity fields. The HttpOnly
 * `tb_onboarding_token` cookie is managed exclusively by the backend —
 * the client never reads or writes it.
 *
 * Cleanup triggers (LD-7):
 *   - successful onboarding submit (caller calls `clear()` post-setUser)
 *   - consent modal cancel
 *   - NavigationEnd to `/auth/login` while `isCrossPlatform()` is true
 *   - app reload (in-memory, implicit)
 */
@Injectable({ providedIn: 'root' })
export class CrossPlatformOnboardingStateService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  // ── Writable signals (private) ──
  private readonly _registeredPlatform = signal<RegisteredPlatform | null>(null);
  private readonly _prefill = signal<CrossPlatformPrefill | null>(null);
  private readonly _accountType = signal<'personal' | 'corporate' | null>(null);

  // ── Read-only signals (public) ──
  readonly registeredPlatform = this._registeredPlatform.asReadonly();
  readonly prefill = this._prefill.asReadonly();
  readonly accountType = this._accountType.asReadonly();

  // ── Computed ──
  readonly isCrossPlatform = computed(() => this._registeredPlatform() !== null);
  readonly isNrxTrustBankingRecovery = computed(
    () => this._registeredPlatform() === 'NRX-TrustBanking',
  );

  constructor() {
    // NavigationEnd to /auth/login while cross-platform — clear state.
    // Guarded so we don't trigger unnecessary set calls on unrelated
    // navigations (LD-7).
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (this.isCrossPlatform() && event.urlAfterRedirects.startsWith('/auth/login')) {
          this.clear();
        }
      });
  }

  // ── Mutators ──

  /**
   * Populate from a verify-2fa Branch B response. Caller MUST have
   * validated the `prefill` block via `validateCrossPlatformPrefill`
   * first (LD-2). `accountType` is reset to null so the user picks it
   * via the account-type wizard page.
   */
  setFromBranchB(res: Verify2faOnboardingResponse): void {
    this._registeredPlatform.set(res.registeredPlatform);
    this._prefill.set(res.prefill);
    this._accountType.set(null);
  }

  setAccountType(type: 'personal' | 'corporate'): void {
    this._accountType.set(type);
  }

  clear(): void {
    this._registeredPlatform.set(null);
    this._prefill.set(null);
    this._accountType.set(null);
  }
}
